import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import './TimesheetForm.css';

const ATTENDANCE = ['Working', 'Leave', 'Company Holiday', 'Weekend', 'Halfday Leave'];

const emptyHeader = {
  employee_id: '', employee_name: '', customer_id: '', msa_id: '',
  billing_month: '', follow_billing_sections: 1, billing_group_id: '',
  period_from: '', period_to: '', rfp_num: '', remarks: ''
};

const emptyEntry = () => ({
  worked_date: '', attendance: 'Working', sub_brand_id: '',
  jira_ticket_num: '', task_description: '', worked_hours: '', claimed_hours: ''
});

export default function TimesheetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyHeader);
  const [entries, setEntries] = useState([emptyEntry()]);
  const [status, setStatus] = useState('Draft');
  const [customers, setCustomers] = useState([]);
  const [msaList, setMsaList] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [subBrands, setSubBrands] = useState([]);
  const [maxHoursPerDay, setMaxHoursPerDay] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [loadingTs, setLoadingTs] = useState(false);

  useEffect(() => {
    api.get('/customers').then(setCustomers).catch(() => {});
    api.get('/msa').then(setMsaList).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) {
      setSubmission(null);
      return;
    }
    let mounted = true;
    setLoadingTs(true);
    api
      .get(`/timesheets/${id}`)
      .then(t => {
        if (!mounted) return;
        setSubmission(t);
        setForm({
          employee_id: t.employee_id || '', employee_name: t.employee_name || '',
          customer_id: t.customer_id || '', msa_id: t.msa_id || '',
          billing_month: t.billing_month || '',
          follow_billing_sections: t.follow_billing_sections ?? 1,
          billing_group_id: t.billing_group_id || '',
          period_from: t.period_from || '', period_to: t.period_to || '',
          rfp_num: t.rfp_num || '', remarks: t.remarks || ''
        });
        setStatus(t.status || 'Draft');
        setEntries(t.entries?.length ? t.entries.map(e => ({
          worked_date: e.worked_date || '', attendance: e.attendance || 'Working',
          sub_brand_id: e.sub_brand_id || '', jira_ticket_num: e.jira_ticket_num || '',
          task_description: e.task_description || '',
          worked_hours: e.worked_hours ?? '', claimed_hours: e.claimed_hours ?? ''
        })) : [emptyEntry()]);
      })
      .catch(() => {
        if (!mounted) return;
        alert('Could not load this timesheet.');
        navigate('/timesheets');
      })
      .finally(() => {
        if (mounted) setLoadingTs(false);
      });
    return () => { mounted = false; };
  }, [id, navigate]);

  // Load MSA-derived data (billing groups, sub-brands, max hours) whenever MSA changes
  useEffect(() => {
    if (!form.msa_id) {
      setBillingGroups([]); setSubBrands([]); setMaxHoursPerDay(null);
      return;
    }
    api.get(`/msa/${form.msa_id}`).then(m => {
      setBillingGroups(m.billing_groups || []);
      setSubBrands((m.sub_brands || []).filter(sb => sb.is_active));
      setMaxHoursPerDay(m.max_billing_hours_per_day || null);
    }).catch(() => {});
  }, [form.msa_id]);

  const customerName = useMemo(
    () => customers.find(c => c.customer_id === form.customer_id)?.legal_name || '',
    [customers, form.customer_id]
  );

  const availableMsas = useMemo(
    () => msaList.filter(m => !form.customer_id || m.customer_id === form.customer_id),
    [msaList, form.customer_id]
  );

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'customer_id') { next.msa_id = ''; next.billing_group_id = ''; }
    if (k === 'msa_id') { next.billing_group_id = ''; }
    if (k === 'follow_billing_sections' && !v) next.billing_group_id = '';
    return next;
  });
  const setEntry = (idx, k, v) => setEntries(rows => rows.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  const addRow = () => setEntries(rows => [...rows, emptyEntry()]);
  const removeRow = (idx) => setEntries(rows => rows.filter((_, i) => i !== idx));

  const totalWorked = useMemo(() => entries.reduce((s, e) => s + (parseFloat(e.worked_hours) || 0), 0), [entries]);
  const totalClaimed = useMemo(() => entries.reduce((s, e) => s + (parseFloat(e.claimed_hours) || 0), 0), [entries]);

  // Cumulative per-day running sums for visual validation
  const dailyRunning = useMemo(() => {
    const byDate = {};
    return entries.map(e => {
      if (!e.worked_date) return 0;
      byDate[e.worked_date] = (byDate[e.worked_date] || 0) + (parseFloat(e.worked_hours) || 0);
      return byDate[e.worked_date];
    });
  }, [entries]);

  const locked = status === 'Submitted' || status === 'Approved' || loadingTs;
  const followBilling = !!form.follow_billing_sections;

  const validateClientSide = () => {
    if (!form.employee_id?.trim()) return 'Employee ID is required';
    if (!form.employee_name?.trim()) return 'Employee Name is required';
    if (!form.customer_id) return 'Customer is required';
    if (!form.period_from || !form.period_to) return 'From Date and To Date are required';
    if (form.period_from > form.period_to) return 'From Date must be <= To Date';
    for (const [i, e] of entries.entries()) {
      const wh = parseFloat(e.worked_hours) || 0;
      const ch = parseFloat(e.claimed_hours) || 0;
      if (!e.worked_date) return `Row ${i + 1}: Date is required`;
      if (e.worked_date < form.period_from || e.worked_date > form.period_to) return `Row ${i + 1}: Date must be within From/To range`;
      if (!e.jira_ticket_num?.trim()) return `Row ${i + 1}: Jira ticket number is mandatory`;
      if (!e.task_description?.trim()) return `Row ${i + 1}: Task description is mandatory`;
      if (wh < 0 || wh > 24) return `Row ${i + 1}: Worked hours must be between 0 and 24`;
      if (maxHoursPerDay && wh > maxHoursPerDay) return `Row ${i + 1}: Worked hours exceed MSA max (${maxHoursPerDay}/day)`;
      if (ch < 0 || ch > wh) return `Row ${i + 1}: Claimed hours must be between 0 and worked hours`;
    }
    if (maxHoursPerDay) {
      const byDate = {};
      for (const [i, e] of entries.entries()) {
        if (!e.worked_date) continue;
        byDate[e.worked_date] = (byDate[e.worked_date] || 0) + (parseFloat(e.worked_hours) || 0);
        if (byDate[e.worked_date] > maxHoursPerDay) return `Row ${i + 1}: Cumulative hours on ${e.worked_date} (${byDate[e.worked_date].toFixed(2)}) exceed MSA max ${maxHoursPerDay}/day`;
      }
    }
    return null;
  };

  const handleSave = async (goBack = true) => {
    const err = validateClientSide();
    if (err) { alert(err); return; }
    try {
      const payload = { ...form, entries };
      if (isEdit) {
        await api.put(`/timesheets/${id}`, payload);
        if (goBack) navigate('/timesheets');
        return id;
      } else {
        const res = await api.post('/timesheets', payload);
        if (goBack) navigate('/timesheets');
        return res.timesheet_id;
      }
    } catch (e) { alert(e.message); }
  };

  const handleSubmitForApproval = async () => {
    const err = validateClientSide();
    if (err) { alert(err); return; }
    try {
      let tsId = id;
      if (!isEdit) {
        tsId = await handleSave(false);
        if (!tsId) return;
      } else {
        await api.put(`/timesheets/${id}`, { ...form, entries });
      }
      await api.post('/approvals/submit', { entity_type: 'Timesheet', entity_id: tsId });
      navigate('/timesheets');
    } catch (e) { alert(e.message); }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this timesheet?')) return;
    try {
      await api.post('/approvals/approve', { entity_type: 'Timesheet', entity_id: id });
      navigate('/timesheets');
    } catch (e) { alert(e.message); }
  };

  const handleReject = async () => {
    try {
      await api.post('/approvals/reject', { entity_type: 'Timesheet', entity_id: id, comments: rejectReason });
      setShowReject(false);
      navigate('/timesheets');
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this timesheet?')) return;
    try {
      await api.del(`/timesheets/${id}`);
      navigate('/timesheets');
    } catch (e) { alert(e.message); }
  };

  const canApprove = submission?.status === 'Submitted';
  const workflowLocked = status === 'Submitted' || status === 'Approved';

  return (
    <>
      <div className="page-header">
        <h1>{isEdit ? `Timesheet ${id}` : 'New Timesheet'}</h1>
        <div className="btn-group">
          {!workflowLocked && !loadingTs && <button type="button" className="btn btn-outline" onClick={() => handleSave(true)}>Save Draft</button>}
          {!workflowLocked && !loadingTs && <button type="button" className="btn btn-primary" onClick={handleSubmitForApproval}>Submit for Approval</button>}
          {isEdit && canApprove && !loadingTs && <button type="button" className="btn btn-primary" onClick={handleApprove}>Approve</button>}
          {isEdit && canApprove && !loadingTs && (
            <button type="button" className="btn btn-outline timesheet-header-danger-btn" onClick={() => setShowReject(true)}>Reject</button>
          )}
          {isEdit && !workflowLocked && !loadingTs && (
            <button type="button" className="btn btn-outline timesheet-header-danger-btn" onClick={handleDelete}>Delete</button>
          )}
        </div>
      </div>
      <div className="page-content timesheet-form-page clause-library-page">
        <nav className="page-back-nav" aria-label="Back navigation">
          <Link to="/timesheets" className="page-back-link">
            ← Back to Timesheets
          </Link>
        </nav>

        {loadingTs && isEdit ? (
          <div className="card timesheet-form-loading" role="status" aria-live="polite">
            <div className="timesheet-form-loading-spinner" aria-hidden />
            <p>Loading timesheet {id}…</p>
          </div>
        ) : (
          <>
            <div className="timesheet-form-summary-strip" aria-label="Timesheet summary">
              <div className="timesheet-summary-card">
                <div className="timesheet-summary-label">Status</div>
                <div className="timesheet-summary-value timesheet-summary-value--badge">
                  <span className={`badge badge-${(status || 'Draft').toLowerCase()}`}>{status}</span>
                </div>
              </div>
              <div className="timesheet-summary-card">
                <div className="timesheet-summary-label">Billing month</div>
                <div className="timesheet-summary-value tabular-nums">{form.billing_month || '—'}</div>
              </div>
              <div className="timesheet-summary-card">
                <div className="timesheet-summary-label">Period</div>
                <div className={`timesheet-summary-value timesheet-form-summary-period ${!(form.period_from && form.period_to) ? 'timesheet-summary-value--muted' : ''}`}>
                  {form.period_from && form.period_to ? `${form.period_from} → ${form.period_to}` : '—'}
                </div>
              </div>
              <div className="timesheet-summary-card">
                <div className="timesheet-summary-label">Worked / claimed hours</div>
                <div className="timesheet-summary-value tabular-nums">
                  {totalWorked.toFixed(2)} / {totalClaimed.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="card timesheet-form-header-card">
              <div className="card-header timesheet-form-section-head">
                <div className="timesheet-form-section-head-main">
                  <span className="timesheet-form-section-title">Timesheet details</span>
                  {workflowLocked ? (
                    <span className="timesheet-form-section-sub muted">Submitted or approved timesheets cannot be edited.</span>
                  ) : (
                    <span className="timesheet-form-section-sub">Employee, billing context, and reporting period.</span>
                  )}
                </div>
                {workflowLocked ? (
                  <span className="timesheet-form-readonly-pill" title="Editing is disabled">Read-only</span>
                ) : null}
              </div>

              <section className="timesheet-form-section-block">
                <h3 className="timesheet-form-section-heading">Employee & customer</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="ts-emp-id">Employee ID *</label>
                    <input id="ts-emp-id" value={form.employee_id} onChange={e => set('employee_id', e.target.value)} disabled={locked} autoComplete="off" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-emp-name">Employee name *</label>
                    <input id="ts-emp-name" value={form.employee_name} onChange={e => set('employee_name', e.target.value)} disabled={locked} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-customer">Customer *</label>
                    <select id="ts-customer" value={form.customer_id} onChange={e => set('customer_id', e.target.value)} disabled={locked}>
                      <option value="">Select customer…</option>
                      {customers.map(c => (
                        <option key={c.customer_id} value={c.customer_id}>
                          {c.customer_id} · {c.legal_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-customer-name">Registered name</label>
                    <input id="ts-customer-name" value={customerName} readOnly title={customerName} className="timesheet-form-readonly-input" />
                  </div>
                </div>
              </section>

              <section className="timesheet-form-section-block">
                <h3 className="timesheet-form-section-heading">Agreement & billing</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="ts-msa">MSA</label>
                    <select id="ts-msa" value={form.msa_id} onChange={e => set('msa_id', e.target.value)} disabled={locked || !form.customer_id}>
                      <option value="">{form.customer_id ? 'Select MSA…' : 'Select customer first…'}</option>
                      {availableMsas.map(m => (
                        <option key={m.msa_id} value={m.msa_id}>
                          {m.msa_id}{m.msa_ref ? ` (${m.msa_ref})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-bill-month">Billing month</label>
                    <input id="ts-bill-month" type="month" value={form.billing_month} onChange={e => set('billing_month', e.target.value)} disabled={locked} />
                  </div>
                  <div className="form-group full-width timesheet-form-checkbox-row">
                    <label htmlFor="ts-follow-bg">
                      <input id="ts-follow-bg" type="checkbox" checked={followBilling} onChange={e => set('follow_billing_sections', e.target.checked ? 1 : 0)} disabled={locked} />
                      Follow billing sections (grouped MSA)
                    </label>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-bg">Billing section</label>
                    <select id="ts-bg" value={form.billing_group_id} onChange={e => set('billing_group_id', e.target.value)} disabled={locked || !followBilling}>
                      <option value="">{followBilling ? 'Select section…' : '—'}</option>
                      {followBilling && billingGroups.map(bg => <option key={bg.id} value={bg.id}>{bg.group_name}</option>)}
                    </select>
                  </div>
                </div>
              </section>

              <section className="timesheet-form-section-block">
                <h3 className="timesheet-form-section-heading">Period & references</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="ts-from">From date *</label>
                    <input id="ts-from" type="date" value={form.period_from} onChange={e => set('period_from', e.target.value)} disabled={locked} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-to">To date *</label>
                    <input id="ts-to" type="date" value={form.period_to} onChange={e => set('period_to', e.target.value)} disabled={locked} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ts-rfp">RFP number</label>
                    <input id="ts-rfp" value={form.rfp_num} onChange={e => set('rfp_num', e.target.value)} disabled={locked} placeholder="Optional" />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="ts-remarks">Remarks</label>
                    <textarea id="ts-remarks" value={form.remarks} onChange={e => set('remarks', e.target.value)} disabled={locked} rows={3} placeholder="Internal notes for this timesheet" />
                  </div>
                </div>
              </section>

              {maxHoursPerDay ? (
                <p className="timesheet-form-msa-hint">
                  MSA max billing hours/day: <strong>{maxHoursPerDay}</strong> — cumulative worked hours on any date cannot exceed this.
                </p>
              ) : null}
            </div>

            <div className="card timesheet-form-entries-card">
              <div className="card-header timesheet-form-entries-toolbar">
                <div className="timesheet-form-entries-heading">
                  <span className="timesheet-form-entries-title">Timesheet entries</span>
                  <span className="timesheet-form-entries-sub">One row per calendar entry · Required fields marked *</span>
                </div>
                <div className="timesheet-form-entries-totals-pill" aria-label="Cumulative hours">
                  <span className="timesheet-form-entries-pill-item">
                    Worked <strong className="tabular-nums">{totalWorked.toFixed(2)}</strong> <span className="timesheet-form-entries-unit">hrs</span>
                  </span>
                  <span className="timesheet-form-entries-pill-sep" aria-hidden />
                  <span className="timesheet-form-entries-pill-item">
                    Claimed <strong className="tabular-nums">{totalClaimed.toFixed(2)}</strong> <span className="timesheet-form-entries-unit">hrs</span>
                  </span>
                </div>
              </div>
              <div className="timesheet-form-entries-wrap">
                <table className="timesheet-form-entries-table">
                  <thead>
                    <tr>
                      <th className="timesheet-form-col-date" scope="col">Date</th>
                      <th className="timesheet-form-col-att" scope="col">Attendance</th>
                      <th className="timesheet-form-col-brand" scope="col">Sub-brand</th>
                      <th className="timesheet-form-col-jira" scope="col" title="Jira ticket (required)">Jira *</th>
                      <th className="timesheet-form-col-task" scope="col" title="Task description (required)">Task *</th>
                      <th className="timesheet-form-col-num" scope="col">Worked</th>
                      <th className="timesheet-form-col-num" scope="col">Claimed</th>
                      {!locked && <th className="timesheet-form-col-actions" scope="col" aria-label="Remove row" />}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, idx) => {
                      const wh = parseFloat(e.worked_hours) || 0;
                      const ch = parseFloat(e.claimed_hours) || 0;
                      const dateOutOfRange = e.worked_date && form.period_from && form.period_to && (e.worked_date < form.period_from || e.worked_date > form.period_to);
                      const whBad = wh < 0 || wh > 24 || (maxHoursPerDay && dailyRunning[idx] > maxHoursPerDay);
                      const chBad = ch < 0 || ch > wh;
                      return (
                        <tr key={idx} className="timesheet-form-entry-row">
                          <td className="timesheet-form-entry-cell timesheet-form-col-date">
                            <input
                              type="date"
                              className={`timesheet-form-entry-input ${dateOutOfRange ? 'timesheet-form-input-invalid' : ''}`}
                              value={e.worked_date}
                              min={form.period_from || undefined}
                              max={form.period_to || undefined}
                              onChange={ev => setEntry(idx, 'worked_date', ev.target.value)}
                              disabled={locked}
                            />
                          </td>
                          <td className="timesheet-form-entry-cell timesheet-form-col-att">
                            <select className="timesheet-form-entry-input" value={e.attendance} onChange={ev => setEntry(idx, 'attendance', ev.target.value)} disabled={locked}>
                              {ATTENDANCE.map(a => <option key={a} value={a}>{a}</option>)}
                            </select>
                          </td>
                          <td className="timesheet-form-entry-cell timesheet-form-col-brand">
                            <select className="timesheet-form-entry-input" value={e.sub_brand_id} onChange={ev => setEntry(idx, 'sub_brand_id', ev.target.value)} disabled={locked}>
                              <option value="">— None —</option>
                              {subBrands.map(sb => <option key={sb.id} value={sb.id}>{sb.brand_name}</option>)}
                            </select>
                          </td>
                          <td className="timesheet-form-entry-cell timesheet-form-col-jira">
                            <input className="timesheet-form-entry-input" value={e.jira_ticket_num} onChange={ev => setEntry(idx, 'jira_ticket_num', ev.target.value)} disabled={locked} placeholder="PROJ-123" />
                          </td>
                          <td className="timesheet-form-entry-cell timesheet-form-col-task">
                            <input className="timesheet-form-entry-input" value={e.task_description} onChange={ev => setEntry(idx, 'task_description', ev.target.value)} disabled={locked} placeholder="Short summary" />
                          </td>
                          <td className="timesheet-form-entry-cell timesheet-form-col-num">
                            <input
                              type="number"
                              step="0.25"
                              min="0"
                              max="24"
                              className={`timesheet-form-entry-input timesheet-form-num-input ${whBad ? 'timesheet-form-input-invalid' : ''}`}
                              value={e.worked_hours}
                              onChange={ev => setEntry(idx, 'worked_hours', ev.target.value)}
                              disabled={locked}
                            />
                          </td>
                          <td className="timesheet-form-entry-cell timesheet-form-col-num">
                            <input
                              type="number"
                              step="0.25"
                              min="0"
                              className={`timesheet-form-entry-input timesheet-form-num-input ${chBad ? 'timesheet-form-input-invalid' : ''}`}
                              value={e.claimed_hours}
                              onChange={ev => setEntry(idx, 'claimed_hours', ev.target.value)}
                              disabled={locked}
                            />
                          </td>
                          {!locked && (
                            <td className="timesheet-form-entry-cell timesheet-form-col-actions timesheet-form-entry-actions">
                              <button type="button" className="timesheet-form-remove-btn" title="Remove row" aria-label="Remove row" onClick={() => removeRow(idx)}>
                                ×
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!locked && (
                <div className="timesheet-form-add-row">
                  <button type="button" className="btn btn-outline btn-sm" onClick={addRow}>
                    + Add row
                  </button>
                </div>
              )}
            </div>

            {submission?.rejection_reason ? (
              <div className="card timesheet-form-rejection-card">
                <div className="card-header">Rejection reason</div>
                <p className="timesheet-form-rejection-body">{submission.rejection_reason}</p>
              </div>
            ) : null}
          </>
        )}
      </div>

      {showReject && (
        <div className="modal-overlay" onClick={() => setShowReject(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Reject Timesheet {id}</h2>
            <div className="form-group">
              <label>Reason for Rejection</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={4} />
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={handleReject} disabled={!rejectReason.trim()}>Confirm Reject</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowReject(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
